import React, { useState, useEffect } from 'react';
import { FaTimes, FaShieldAlt } from 'react-icons/fa';

const AdminModal = ({ isOpen, onClose, onSubmit, result, loading, clubs, subDistricts = [], showRoleSelection = false }) => {
    const [formData, setFormData] = useState(result || {});

    useEffect(() => {
        setFormData(result || {});
    }, [result, isOpen]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    if (!isOpen) return null;

    const isClubAdmin = formData.role === 'club_admin' || (!formData.role && !showRoleSelection);
    const isDistrictAdmin = formData.role === 'district_admin';

    const inputClasses = "w-full px-5 py-3.5 rounded-xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-normal";
    const labelClasses = "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1";

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/10 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose}>
            <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-slate-100 transform transition-all animate-premium" onClick={(e) => e.stopPropagation()}>
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white">
                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                        {formData.id ? 'Modify Credentials' : 'Initialize Administrator'}
                    </h2>
                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all">
                        <FaTimes size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className={labelClasses}>Full Intelligence Name</label>
                            <input
                                type="text"
                                value={formData.name || ''}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter full name"
                                className={inputClasses}
                            />
                        </div>

                        <div>
                            <label className={labelClasses}>System Access Email</label>
                            <input
                                type="email"
                                value={formData.email || ''}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                placeholder="admin@domain.com"
                                className={inputClasses}
                            />
                        </div>

                        {!formData.id && (
                            <div>
                                <label className={labelClasses}>Security Initialization Key</label>
                                <input
                                    type="password"
                                    value={formData.password || ''}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                    placeholder="••••••••"
                                    className={inputClasses}
                                />
                            </div>
                        )}

                        {showRoleSelection && (
                            <div>
                                <label className={labelClasses}>Authorization Tier</label>
                                <select
                                    value={formData.role || ''}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                    required
                                    className={inputClasses}
                                >
                                    <option value="">Select Security Level</option>
                                    <option value="district_admin">District Controller</option>
                                    <option value="club_admin">Club Admin</option>
                                </select>
                            </div>
                        )}

                        {isDistrictAdmin && subDistricts.length > 0 && (
                            <div>
                                <label className={labelClasses}>Assigned Sub-Sector</label>
                                <select
                                    value={formData.sub_district_id || ''}
                                    onChange={(e) => setFormData({ ...formData, sub_district_id: e.target.value, district_id: 1 })}
                                    required
                                    className={inputClasses}
                                >
                                    <option value="">Select Operational Sub-District</option>
                                    {subDistricts.map((sd) => (
                                        <option key={sd.id} value={sd.id}>{sd.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {isClubAdmin && (
                            <>
                                <div>
                                    <label className={labelClasses}>Affiliated Deployment Club</label>
                                    <select
                                        value={formData.club_id || ''}
                                        onChange={(e) => {
                                            const club = clubs.find(c => c.lion_ID === e.target.value || c.id == e.target.value);
                                            setFormData({
                                                ...formData,
                                                club_id: e.target.value,
                                                sub_district_id: club?.sub_district_id,
                                            });
                                        }}
                                        required
                                        className={inputClasses}
                                    >
                                        <option value="">Select Tactical Club</option>
                                        {clubs.map((club) => (
                                            <option key={club.lion_ID || club.id} value={club.lion_ID || club.id}>{club.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {(!showRoleSelection || formData.position) && (
                                    <div>
                                        <label className={labelClasses}>Organizational Rank</label>
                                        <select
                                            value={formData.position || 'President'}
                                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                            required
                                            className={inputClasses}
                                        >
                                            <option value="President">President</option>
                                            <option value="Vice President">Vice President</option>
                                            <option value="Secretary">Secretary</option>
                                            <option value="Treasurer">Treasurer</option>
                                            <option value="Executive Member">Executive Member</option>
                                            <option value="Board Member">Board Member</option>
                                            <option value="Member">Member</option>
                                        </select>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex gap-4 pt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-4 text-slate-400 font-bold text-[10px] uppercase tracking-widest hover:text-slate-900 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-slate-900/20 hover:bg-black hover:-translate-y-0.5 transition-all disabled:opacity-50"
                        >
                            {loading ? 'Processing...' : (formData.id ? 'Commit Changes' : 'Initialize Access')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminModal;
